import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormControl, FormLabel, InputAdornment, Paper, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { DialogType } from '../../../@types';
import { AffiliateBonusConfig } from '../../../@types/api';
import {
  AFFILIATE_BONUS_CATEGORY_OPTIONS,
  AFFILIATE_BONUS_REWARD_TYPE_OPTIONS,
  CAMPAIGN_BONUS_TYPE_OPTIONS,
  QUERY_KEYS,
} from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { AFFILIATE_BONUS_CATEGORY, AFFILIATE_BONUS_REWARD_TYPE } from '../../../constants/enums';
import useMutateData from '../../../hooks/useMutateData';
import { AffiliateBonusFormData, affiliateBonusFormSchema } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormSelect from '../../molecules/FormSelect';
import { getData } from '../../../utils/api';
import { useMarketplace } from '../../../hooks/useMarketplace';

const DEFAULT_VALUES: AffiliateBonusFormData = {
  campaignBonusType: 'FIRST_TIME_DEPOSIT',
  rewardType: AFFILIATE_BONUS_REWARD_TYPE.BONUS_CREDITS,
  bonusCategory: AFFILIATE_BONUS_CATEGORY.PERCENTAGE,
  bonusPercentage: null,
  fixedAmount: null,
  capLimit: 0,
  minDepositAmount: 1,
  timeframe: 1,
  sbWagering: 1,
  casinoWagering: 1,
};

const styles = {
  section: {
    p: 2,
    borderRadius: 2,
    bgcolor: 'action.hover',
  },
  sectionTitle: {
    typography: 'h5',
  },
  fieldLabel: {
    typography: 'subtitle2',
    mb: 1,
  },
} as const;

const getFormValues = (
  settings: Partial<AffiliateBonusConfig> | undefined,
  isStandaloneCasino: boolean
): AffiliateBonusFormData => {
  const { id: _id, ...savedSettings } = settings ?? {};

  return {
    ...DEFAULT_VALUES,
    ...savedSettings,
    rewardType: isStandaloneCasino
      ? AFFILIATE_BONUS_REWARD_TYPE.REAL_MONEY
      : (savedSettings.rewardType ?? DEFAULT_VALUES.rewardType),
    bonusCategory:
      savedSettings.fixedAmount != null ? AFFILIATE_BONUS_CATEGORY.FIXED_AMOUNT : DEFAULT_VALUES.bonusCategory,
    timeframe: savedSettings.timeframe ?? DEFAULT_VALUES.timeframe,
    casinoWagering: savedSettings.casinoWagering ?? DEFAULT_VALUES.casinoWagering,
    sbWagering: isStandaloneCasino ? null : (savedSettings.sbWagering ?? DEFAULT_VALUES.sbWagering),
  };
};

const AffiliateGlobalBonusForm = ({ closeModal }: DialogType) => {
  const { createData, updateData } = useMutateData(API_ROUTES.CAMPAIGN_BONUS, [QUERY_KEYS.bonus], 'crm');
  const { config, isStandaloneCasino } = useMarketplace();
  const { data: globalBonusSettings } = useQuery<AffiliateBonusConfig>({
    queryKey: [QUERY_KEYS.bonus],
    queryFn: () => getData(API_ROUTES.CAMPAIGN_BONUS_GLOBAL, {}, 'crm'),
  });

  const currency = config?.marketplaceConfig?.currency || 'EUR';
  const formValues = useMemo(
    () => getFormValues(globalBonusSettings, isStandaloneCasino),
    [globalBonusSettings, isStandaloneCasino]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<input<typeof affiliateBonusFormSchema>, unknown, AffiliateBonusFormData>({
    defaultValues: formValues,
    resolver: zodResolver(affiliateBonusFormSchema),
  });

  useEffect(() => {
    reset(formValues);
  }, [formValues, reset]);

  const bonusCategory = watch('bonusCategory');
  const isPercentageSelected = bonusCategory === AFFILIATE_BONUS_CATEGORY.PERCENTAGE;

  const rewardTypeOptions = isStandaloneCasino
    ? AFFILIATE_BONUS_REWARD_TYPE_OPTIONS.filter((option) => option.id === AFFILIATE_BONUS_REWARD_TYPE.REAL_MONEY)
    : AFFILIATE_BONUS_REWARD_TYPE_OPTIONS;

  const handleCancel = () => {
    closeModal?.();
    reset(formValues);
  };

  const onFormSubmit = ({ bonusCategory: category, ...data }: AffiliateBonusFormData) => {
    const payload = {
      ...data,
      rewardType: isStandaloneCasino ? AFFILIATE_BONUS_REWARD_TYPE.REAL_MONEY : data.rewardType,
      bonusPercentage: category === AFFILIATE_BONUS_CATEGORY.PERCENTAGE ? data.bonusPercentage : null,
      fixedAmount: category === AFFILIATE_BONUS_CATEGORY.FIXED_AMOUNT ? data.fixedAmount : null,
      sbWagering: isStandaloneCasino ? null : data.sbWagering,
    };

    if (globalBonusSettings) {
      updateData(globalBonusSettings.id, payload, handleCancel);
    } else {
      createData(payload, handleCancel);
    }
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      onClose={handleCancel}
      label='Set global bonus'
      showEditTabs={false}
    >
      <FormSelect
        label='Campaign Bonus Type'
        name='campaignBonusType'
        control={control}
        error={errors.campaignBonusType}
        options={CAMPAIGN_BONUS_TYPE_OPTIONS}
        required
        allowDeselect={false}
      />
      <FormSelect
        label='Awarded Bonus Type'
        name='rewardType'
        control={control}
        error={errors.rewardType}
        options={rewardTypeOptions}
        required
        allowDeselect={false}
      />

      <Paper variant='outlined' sx={styles.section}>
        <Stack spacing={2}>
          <FormLabel sx={styles.sectionTitle}>Bonus Amount</FormLabel>
          <FormControl fullWidth>
            <ToggleButtonGroup
              value={bonusCategory}
              onChange={(_e, value) =>
                value && setValue('bonusCategory', value, { shouldDirty: true, shouldValidate: true })
              }
              exclusive
              fullWidth
            >
              {AFFILIATE_BONUS_CATEGORY_OPTIONS.map((option) => (
                <ToggleButton key={option.id} value={option.id}>
                  {option.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FormControl>
          {isPercentageSelected ? (
            <FormNumberInput
              key='bonusPercentage'
              error={errors.bonusPercentage}
              helperText={errors.bonusPercentage?.message}
              label='Bonus Percentage'
              control={control}
              name='bonusPercentage'
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                },
              }}
            />
          ) : (
            <FormNumberInput
              key='fixedAmount'
              error={errors.fixedAmount}
              helperText={errors.fixedAmount?.message}
              label='Fixed Amount'
              control={control}
              name='fixedAmount'
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                },
              }}
            />
          )}
        </Stack>
      </Paper>

      <FormNumberInput
        error={errors.capLimit}
        helperText={errors.capLimit?.message}
        label='Cap Limit'
        control={control}
        name='capLimit'
        slotProps={{
          input: {
            endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
          },
        }}
      />
      <FormNumberInput
        error={errors.minDepositAmount}
        helperText={errors.minDepositAmount?.message}
        label='Minimum Deposit Amount'
        control={control}
        name='minDepositAmount'
        slotProps={{
          input: {
            endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
          },
        }}
      />
      <FormNumberInput
        error={errors.timeframe}
        helperText={errors.timeframe?.message}
        label='Timeframe'
        control={control}
        name='timeframe'
        allowDecimals={false}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position='end'>days</InputAdornment>,
          },
        }}
      />
      {!isStandaloneCasino && (
        <FormNumberInput
          error={errors.sbWagering}
          helperText={errors.sbWagering?.message}
          label='Sportsbook Wagering Req.'
          control={control}
          name='sbWagering'
          slotProps={{
            input: {
              endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
            },
          }}
        />
      )}
      <FormNumberInput
        error={errors.casinoWagering}
        helperText={errors.casinoWagering?.message}
        label='Casino Wagering Req.'
        control={control}
        name='casinoWagering'
        slotProps={{
          input: {
            endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
          },
        }}
      />
    </FormModalLayout>
  );
};

export default AffiliateGlobalBonusForm;
